"use client";

import {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import AppWrapper from "@/shared/ui/components/app-wrapper";
import Button from "@/shared/ui/components/button";
import { DocumentsModalNew } from "@/modules/payment-methods/components/documents-modal";
import { WalletBoxNew } from "@/modules/payment-methods/components/wallet-box";
import LoadingPage from "@/shared/ui/components/loading-page";
import ApplePayModal from "@/shared/ui/components/apple-pay-modal";
import { ErrorScreen } from "@/shared/ui/layout/error-screen";
import Banner, { BannerType } from "@/shared/ui/components/banner";
import {
  type AppContext,
  type CheckoutSession,
  LOCALIZED_ICON_TYPE,
  CheckoutStatus,
  CHECKOUT_STATUS_PROCESSING_PAYMENT,
  Currency,
  SessionError,
  Page,
} from "@/types";
import { parsePaymentResult as parsePaymentResultLib } from "@/lib/payment-result";
import {
  useCheckoutSessionV3,
  usePaymentPlansActions,
  useSavedCards,
  useSelectedPaymentMethod,
  useBackFromPaymentMethodSelection,
} from "@/modules/checkout-session";
import { logError } from "@/services/logging/logger";
import { useScreenTracking } from "@/shared/hooks/use-screen-tracking";
import { useBrowserBackButton } from "@/shared/hooks/use-browser-back-button";
import i18n from "@/i18n";
import enUSErrors from "@/locale/en_US.errors.json";
import arSAErrors from "@/locale/ar_SA.errors.json";
import * as events from "@/events";
import {
  emitNavigationTracking,
  emitCardFormTracking,
  emitModalTracking,
} from "@/shared/tracking/analytics";
import { PaymentMethodUtils } from "@/modules/payment-methods/services/payment-method-utils";
import {
  PaymentMethodType,
  Processing,
  PageError,
} from "@/modules/payment-methods/types";
import { usePaymentMethods } from "@/modules/payment-methods/hooks/use-payment-methods";
import { usePaymentSubmission } from "@/modules/payment-methods/hooks/use-payment-submission";
import { usePaymentModals } from "@/modules/payment-methods/hooks/use-payment-modals";
import { usePaymentPolling } from "@/modules/payment-methods/hooks/use-payment-polling";
import { use3dsFailureReporting } from "@/modules/payment-methods/hooks/use-3ds-failure-reporting";
import { useWalletCardSync } from "@/modules/payment-methods/hooks/use-wallet-card-sync";
import { useSlowPaymentTracking } from "@/modules/payment-methods/hooks/use-slow-payment-tracking";
import { usePaymentMethodsTracking } from "@/modules/payment-methods/hooks/use-payment-methods-tracking";
import { PaymentMethodList } from "@/modules/payment-methods/components/payment-method-list";
import { PaymentFooter } from "@/modules/payment-methods/components/payment-footer";
import { CvvModal } from "@/modules/payment-methods/components/cvv-modal";
import { InlineCardForm } from "@/modules/payment-methods/components/inline-card-form";
import { RepaymentDueCard } from "@/modules/payment-methods/components/repayment-due-card";
import { KeyFactsAcknowledgementLine } from "@/modules/payment-methods/components/key-facts-acknowledgement-line";
import { KeyFactsStatementModal } from "@/modules/payment-methods/components/key-facts-statement-modal";
import {
  partitionCheckoutDocuments,
  selectKeyFactsStatementDocument,
  shouldShowKfsAcknowledgementLine,
} from "@/lib/checkout-documents";
import { isUaeBnplPlusProduct } from "@/lib/payment-option";
import MoneyAmount, {
  getCurrencySymbol,
  stringToCurrency,
} from "@/shared/ui/components/money-amount";
import { CancelIcon } from "@/shared/ui/icons/cancel-icon";
import { GreenWalletIcon } from "@/shared/ui/icons/green-wallet-icon";
import CircularProgress from "@mui/material/CircularProgress";
import { InfoOutlinedIcon } from "@/shared/ui/icons/info-outlined-icon";

type PaymentErrorContent = {
  message: string;
};

type PaymentErrorLocale = {
  payment: Record<string, PaymentErrorContent>;
};

const paymentErrorLocales: Record<string, PaymentErrorLocale> = {
  en_US: enUSErrors as PaymentErrorLocale,
  ar_SA: arSAErrors as PaymentErrorLocale,
};

const getPaymentErrorMessage = (code: string | undefined): string => {
  // TODO: Refactor locale resolution into a shared util (e.g. getLocaleLanguage()) — this pattern is repeated in payment-confirmation.tsx and error-screen.tsx.
  const language = i18n.language?.startsWith("ar") ? "ar_SA" : "en_US";
  const locale = paymentErrorLocales[language] ?? paymentErrorLocales.en_US;
  const paymentErrors = locale.payment;

  if (code && paymentErrors[code]) {
    return paymentErrors[code].message;
  }

  return paymentErrors.UNKNOWN.message;
};

export interface SinglePageTopSlotOptions {
  isWalletSelected: boolean;
}

export interface FooterAbovePaySlotOptions {
  processing: Processing;
}

interface PaymentConfirmationNewProps {
  onSessionFetched: (session: CheckoutSession) => void;
  /** Rendered as the first child inside the page body (the pre-selected plan summary). Receives the AppWrapper ctx. */
  topSlot?: (
    ctx: AppContext,
    slotOptions: SinglePageTopSlotOptions,
  ) => ReactNode;
  /** Rendered in the sticky footer directly above Pay (single-page perks / Farah banner). */
  footerAbovePaySlot?: (
    ctx: AppContext,
    slotOptions: FooterAbovePaySlotOptions,
  ) => ReactNode;
  /** Hide header back button AND disable browser-back (single page checkout: no back / no plan change). */
  hideBack?: boolean;
  /** Override the payment-section heading (single page PiF shows "Payment method" instead of "Pay X today"). */
  paymentHeading?: ReactNode;
  /** Hide the "Cancel within 14 days for a full refund" line (single page PiF per Figma). */
  hideCancelMessage?: boolean;
}

/**
 * New modular payment confirmation component
 * Single responsibility: Orchestrating payment confirmation flow
 */
export function PaymentMethodsView({
  onSessionFetched,
  topSlot,
  footerAbovePaySlot,
  hideBack = false,
  paymentHeading,
  hideCancelMessage = false,
}: PaymentConfirmationNewProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Failure loading detection
  const shouldShowFailureLoading = useMemo(() => {
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const result = searchParams.get("result");
    const is3dsFailure = pathname.includes("3ds") && result === "failed";
    const isPaymentMethodFailure =
      pathname.includes("instalment/add-payment-method") &&
      pathname.includes("failed");
    return is3dsFailure || isPaymentMethodFailure;
  }, [location.pathname, location.search]);

  // Session and data
  const { data: session, mutate: mutateSession } = useCheckoutSessionV3({
    skip: shouldShowFailureLoading,
  });
  const ivrStatus = session?.ivrStatus;
  const isIvrFailed = useMemo(
    () => (ivrStatus ?? "").toUpperCase() === "FAILED",
    [ivrStatus],
  );

  // 3DS failure reporting (extracted hook)
  const isReporting3dsFailure = use3dsFailureReporting({
    shouldShowFailureLoading,
    mutateSession,
    onSessionFetched,
  });

  // Payment methods
  const selectedPaymentMethod = useSelectedPaymentMethod();

  const { setSelectedPaymentMethod } = usePaymentPlansActions();
  const { data: savedCards } = useSavedCards({
    merchantId: session?.merchant?.id || "",
    orderId: session?.orderId,
    skip: shouldShowFailureLoading,
  });

  // Wallet auto-selection (does not clear card/Apple Pay selection)
  const { isWalletSelected, setIsWalletSelected } = useWalletCardSync({
    session,
  });

  /**
   * True only when the wallet is toggled on AND it can cover the full down
   * payment. In this state the card / Apple Pay chooser is shown but faded and
   * non-interactive, and payment is submitted as wallet-only.
   */
  const walletCoversDownPayment = PaymentMethodUtils.shouldPayWithWalletOnly(
    isWalletSelected,
    session?.paymentDetails?.canWalletCoverDownPayment,
  );

  // Detect PNM (Pay Next Month) - tenure === 1
  const selectedPaymentOption = useMemo(
    () =>
      (session?.paymentOptions ?? []).find(
        (option) => option.offerId === (session?.selectedPaymentOptionId ?? ""),
      ),
    [session?.paymentOptions, session?.selectedPaymentOptionId],
  );
  const isUaeBnplPlus = useMemo(
    () =>
      isUaeBnplPlusProduct(
        session?.order?.countryCode,
        selectedPaymentOption?.productType,
      ),
    [session?.order?.countryCode, selectedPaymentOption?.productType],
  );
  const isPnm = selectedPaymentOption?.tenure === 1;

  const hasDocuments = (session?.documents?.length ?? 0) > 0;

  const { murabahaDocuments } = useMemo(
    () => partitionCheckoutDocuments(session?.documents),
    [session?.documents],
  );

  const keyFactsDocument = useMemo(
    () =>
      session
        ? selectKeyFactsStatementDocument(session.documents, i18n.language)
        : null,
    [session, i18n.language],
  );

  /** Murabaha checkbox + documents sheet: needs_document_signing + non–KFS documents only. */
  const showMurabahaDocumentSigningCheckbox =
    selectedPaymentOption?.needsDocumentSigning === true &&
    hasDocuments &&
    murabahaDocuments.length > 0;

  /** KFS: show whenever BE sends a Key Facts document (independent of needs_document_signing / tenure). */
  const showKfsAcknowledgementLine = useMemo(
    () => shouldShowKfsAcknowledgementLine(session?.documents, i18n.language),
    [session?.documents, i18n.language],
  );

  // Payment methods hook - using a default value, will be recomputed when ctx is available
  const canPayWithAppleDefault = useMemo(() => {
    if (isPnm) return false;
    return (
      session?.country?.applePayAllowed &&
      "ApplePaySession" in window &&
      typeof (window as { ApplePaySession?: unknown }).ApplePaySession !==
        "undefined" &&
      (savedCards?.metaData?.applePayPreference ?? true)
    );
  }, [session, savedCards, isPnm]);

  const { paymentMethods, paymentMethodsRef, handleSelectPaymentMethod } =
    usePaymentMethods(
      savedCards?.cards ?? [],
      canPayWithAppleDefault || false,
      selectedPaymentMethod,
    );

  // PNM never uses Apple Pay; clear stale apple_pay selection from a prior step
  useEffect(() => {
    if (!isPnm || selectedPaymentMethod?.type !== "apple_pay") return;
    if (savedCards === undefined) return;

    if (savedCards.cards?.length) {
      handleSelectPaymentMethod({
        paymentMethodType: PaymentMethodType.STORED_CARD,
        card: savedCards.cards[0],
      });
    } else {
      setSelectedPaymentMethod(undefined);
    }
  }, [
    isPnm,
    selectedPaymentMethod?.type,
    savedCards,
    handleSelectPaymentMethod,
    setSelectedPaymentMethod,
  ]);

  // Payment submission hook
  const {
    processing,
    submitWithExistingCard,
    submitWithApplePay,
    submitWithWalletOnly,
    tokenizeAndSubmitCard,
  } = usePaymentSubmission(session, onSessionFetched);

  // Modals hook
  const {
    cvvModalOpen,
    isApplePayModalOpen,
    isBottomSheetOpen,
    isBottomSheetClosing,
    hasManuallyClosedApplePayModalRef,
    openCvvModal,
    closeCvvModal,
    openApplePayModal,
    closeApplePayModal,
    openBottomSheet,
    closeBottomSheet,
  } = usePaymentModals();

  const [documentModalKind, setDocumentModalKind] = useState<
    "kfs" | "murabaha" | null
  >(null);

  const openKfsDocumentModal = useCallback(() => {
    setDocumentModalKind("kfs");
    openBottomSheet();
  }, [openBottomSheet]);

  const openMurabahaDocumentModal = useCallback(() => {
    setDocumentModalKind("murabaha");
    openBottomSheet();
  }, [openBottomSheet]);

  useEffect(() => {
    if (!isBottomSheetOpen) {
      setDocumentModalKind(null);
    }
  }, [isBottomSheetOpen]);

  // State
  const [isAgreed, setIsAgreed] = useState(false);
  const [showError, setShowError] = useState(false);
  const [cvv, setCvv] = useState("");
  const [cardForm, setCardForm] = useState({
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    cardholderName: "",
    saveCard: false,
  });
  const [error, setError] = useState<PageError>(PageError.NONE);
  const [submittingGoBack, setSubmittingGoBack] = useState(false);
  const [showInlineCardForm, setShowInlineCardForm] = useState(false);
  const [showNewCardFormView, setShowNewCardFormView] = useState(false);
  const hasAgreementBeenInteractedRef = useRef(false);

  const { trigger: backFromPaymentMethodSelection } =
    useBackFromPaymentMethodSelection();

  useEffect(() => {
    if (
      savedCards !== undefined &&
      paymentMethods.length === 0 &&
      !selectedPaymentMethod &&
      !showInlineCardForm
    ) {
      setShowInlineCardForm(true);
      if (session) {
        emitCardFormTracking("new_card_form_opened", session);
      }
    }
  }, [
    savedCards,
    paymentMethods,
    selectedPaymentMethod,
    showInlineCardForm,
    session,
  ]);

  // Refs
  const ctxRef = useRef<AppContext | null>(null);
  const [screenCtx, setScreenCtx] = useState<AppContext | null>(null);
  const hasInitializedScreenCtxRef = useRef(false);
  const applePayClickHandlerRef = useRef<(() => Promise<void>) | null>(null);

  // Screen tracking
  useScreenTracking({
    screenName: "payment_confirmation",
    viewedEvent: events.PAYMENT_CONFIRMATION_SCREEN_VIEWED,
    exitedEvent: events.PAYMENT_CONFIRMATION_SCREEN_EXITED,
    ctx: screenCtx,
  });

  // Initialize screen context
  useEffect(() => {
    if (!hasInitializedScreenCtxRef.current && ctxRef.current) {
      setScreenCtx(ctxRef.current);
      hasInitializedScreenCtxRef.current = true;
    }
  }, []);

  // Payment polling
  usePaymentPolling(session, ctxRef, onSessionFetched);

  // Extracted hooks for tracking (replaces 5 inline useEffects)
  usePaymentMethodsTracking(session, savedCards);
  useSlowPaymentTracking(session);

  // Loading states
  const has3dsPassed = useMemo(() => {
    const pathname = location.pathname;
    const isPaymentMethodPath = pathname.includes(
      "instalment/add-payment-method/",
    );
    const hasSuccess = pathname.includes("success");
    return isPaymentMethodPath && hasSuccess;
  }, [location.pathname]);

  const isProcessingPayment = CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
    session?.status ?? CheckoutStatus.UNKNOWN,
  );

  const shouldShowLoading =
    isProcessingPayment ||
    has3dsPassed ||
    isReporting3dsFailure ||
    submittingGoBack;

  // Card form validation
  const validateCardForm = () => {
    const cardNumberDigits = cardForm.cardNumber.replace(/\s/g, "");
    const isCardLengthValid = [15, 16].includes(cardNumberDigits.length);
    const isCardNumberValid =
      isCardLengthValid && /^\d+$/.test(cardNumberDigits);

    const isExpiryDateValid =
      cardForm.expiryDate.length === 5 &&
      /^\d{2}\/\d{2}$/.test(cardForm.expiryDate);

    let hasFutureExpiry = false;
    if (isExpiryDateValid) {
      const [month, year] = cardForm.expiryDate.split("/");
      const expiryMonth = parseInt(month, 10);
      const expiryYear = 2000 + parseInt(year, 10);

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      if (expiryYear > currentYear) {
        hasFutureExpiry = true;
      } else if (expiryYear === currentYear && expiryMonth >= currentMonth) {
        hasFutureExpiry = true;
      }
    }

    const isCvvValid =
      cardForm.cvv.length >= 3 &&
      cardForm.cvv.length <= 4 &&
      /^\d+$/.test(cardForm.cvv);

    return (
      isCardNumberValid && isExpiryDateValid && hasFutureExpiry && isCvvValid
    );
  };

  // Computed values (needed for handlers)
  const documentSignatureIsPending =
    showMurabahaDocumentSigningCheckbox && !isAgreed;

  // Handlers
  const handleAddCard = async () => {
    if (!session) return;

    try {
      setError(PageError.NONE);

      await tokenizeAndSubmitCard(
        {
          cardNumber: cardForm.cardNumber,
          expiryDate: cardForm.expiryDate,
          cvv: cardForm.cvv,
        },
        {
          termsAccepted: documentSignatureIsPending ? isAgreed : true,
          documentsSigned: !documentSignatureIsPending,
          shouldUseWalletBalance: isWalletSelected,
        },
        session.customerId ?? "",
        (err) => {
          setError(PageError.TOKENIZATION_FAILED);
          throw err;
        },
      );

      setSelectedPaymentMethod({
        type: "card",
        id: "new",
        name: "",
        last4: cardForm.cardNumber.slice(-4),
        icon: "",
      });

      // Clear form after successful submission
      setCardForm({
        cardNumber: "",
        expiryDate: "",
        cvv: "",
        cardholderName: "",
        saveCard: false,
      });
      setShowInlineCardForm(false);
    } catch {
      // Error handled in tokenizeAndSubmitCard
    }
  };

  const handlePayClick = async () => {
    if (!ctxRef.current || !session) return;

    const ctx = ctxRef.current;

    // PNM with saved cards: submit with the auto-selected card ID but skip CVV entirely
    if (isPnm && savedCards?.cards && savedCards.cards.length > 0) {
      const pnmCard =
        savedCards.cards.find(
          (c) => String(c.id) === selectedPaymentMethod?.id,
        ) ?? savedCards.cards[0];

      return await submitWithExistingCard(
        String(pnmCard.id),
        {
          termsAccepted: documentSignatureIsPending ? isAgreed : true,
          documentsSigned: !documentSignatureIsPending,
          shouldUseWalletBalance: false,
        },
        undefined,
        pnmCard.fingerprint,
        pnmCard,
      );
    }

    // Handle wallet-only payment when wallet fully covers the down payment
    if (
      PaymentMethodUtils.shouldPayWithWalletOnly(
        isWalletSelected,
        ctx.session.paymentDetails?.canWalletCoverDownPayment,
      )
    ) {
      return await submitWithWalletOnly({
        termsAccepted: documentSignatureIsPending ? isAgreed : true,
        documentsSigned: !documentSignatureIsPending,
      });
    }

    // Handle Apple Pay (never for PNM; stale apple_pay falls through to add-card flow)
    if (selectedPaymentMethod?.type === "apple_pay") {
      if (isPnm) {
        if (showInlineCardForm && validateCardForm()) {
          return await handleAddCard();
        }
        setShowInlineCardForm(true);
        if (session) {
          emitCardFormTracking("new_card_form_opened", session);
        }
        return;
      }
      return await submitWithApplePay(ctx, isWalletSelected, {
        termsAccepted: documentSignatureIsPending ? isAgreed : true,
        documentsSigned: !documentSignatureIsPending,
        shouldUseWalletBalance: isWalletSelected,
      });
    }

    // Handle new card form submission if form is filled
    const pnmNoSavedCardsReady =
      isPnm &&
      savedCards !== undefined &&
      !(savedCards.cards && savedCards.cards.length > 0);
    if (
      (!selectedPaymentMethod || selectedPaymentMethod.id === "new") &&
      validateCardForm() &&
      (showInlineCardForm || pnmNoSavedCardsReady)
    ) {
      return await handleAddCard();
    }

    // Handle card payment
    if (!selectedPaymentMethod) {
      // Show inline card form if no payment method selected
      setShowInlineCardForm(true);
      if (session) {
        emitCardFormTracking("new_card_form_opened", session);
      }
      return;
    }

    if (selectedPaymentMethod.id === "new") {
      if (showInlineCardForm) return;
      setShowInlineCardForm(true);
      if (session) {
        emitCardFormTracking("new_card_form_opened", session);
      }
      return;
    }

    const paymentMethod = (paymentMethodsRef.current ?? []).find(
      (m) => (m.card?.id ?? 0) + "" === selectedPaymentMethod.id,
    );

    if (!paymentMethod) {
      logError(
        "payment-confirmation-new",
        "Could not find payment method object",
        undefined,
        { selectedPaymentMethodId: selectedPaymentMethod.id },
      );
      return;
    }

    if (paymentMethod.card?.isDisabledCvv ?? false) {
      return await submitWithExistingCard(
        selectedPaymentMethod.id,
        {
          termsAccepted: documentSignatureIsPending ? isAgreed : true,
          documentsSigned: !documentSignatureIsPending,
          shouldUseWalletBalance: isWalletSelected,
        },
        undefined,
        paymentMethod.card?.fingerprint,
        paymentMethod.card,
      );
    }

    ctx.sendEvent(events.CVV_MODAL_SHOWN);
    if (session) {
      emitModalTracking("cvv_modal_opened", session, {
        modal_name: "cvv",
      });
    }
    openCvvModal();
  };

  const handleCvvSubmit = async () => {
    if (!session || !selectedPaymentMethod) return;

    const selectedCard = savedCards?.cards?.find(
      (card) => String(card.id) === selectedPaymentMethod.id,
    );

    try {
      await submitWithExistingCard(
        selectedPaymentMethod.id ?? "",
        {
          termsAccepted: documentSignatureIsPending ? isAgreed : true,
          documentsSigned: !documentSignatureIsPending,
          shouldUseWalletBalance: isWalletSelected,
        },
        cvv,
        selectedCard?.fingerprint,
        selectedCard,
      );

      closeCvvModal();
      setCvv("");
    } catch (error) {
      logError(
        "payment-confirmation-new",
        "Error processing CVV payment",
        error instanceof Error ? error : new Error(String(error)),
        { error },
      );
      closeCvvModal();
      setCvv("");
    }
  };

  const handleInlineCardFormBack = useCallback(() => {
    setShowNewCardFormView(false);
    setShowInlineCardForm(false);
    if (selectedPaymentMethod?.id === "new") {
      if (paymentMethods.length > 0) {
        handleSelectPaymentMethod(paymentMethods[0]);
      } else {
        setSelectedPaymentMethod(undefined);
      }
    }
  }, [
    selectedPaymentMethod?.id,
    paymentMethods,
    handleSelectPaymentMethod,
    setSelectedPaymentMethod,
  ]);

  const handleGoBack = useCallback(async () => {
    const ctx = ctxRef.current;

    if (ctx) {
      ctx.sendEvent(events.BROWSER_BACK_CLICKED);
    }

    if (session) {
      emitNavigationTracking("back_button_clicked", session, {
        from_page: "payment_confirmation",
      });
    }

    if (submittingGoBack) {
      return;
    }

    // If user is on the add new card form view (with saved cards present),
    // go back to the payment method list instead of plan selection
    if (showNewCardFormView) {
      handleInlineCardFormBack();
      return;
    }

    setSubmittingGoBack(true);

    try {
      const updatedSession = await backFromPaymentMethodSelection();
      setSubmittingGoBack(false);
      onSessionFetched(updatedSession);
    } catch (error) {
      setSubmittingGoBack(false);
      logError(
        "payment-confirmation-new",
        "Failed to go back from payment method selection",
        error instanceof Error ? error : new Error(String(error)),
        { error },
      );
      if (ctx) {
        ctx.navigate(Page.PAYMENT_PLAN_SELECTION);
      }
    }
  }, [
    session,
    submittingGoBack,
    showNewCardFormView,
    handleInlineCardFormBack,
    backFromPaymentMethodSelection,
    onSessionFetched,
  ]);

  useBrowserBackButton({
    onBack: handleGoBack,
    enabled:
      !shouldShowLoading &&
      processing === Processing.NONE &&
      !submittingGoBack &&
      !hideBack,
  });

  const handleHeaderBackClick = useCallback(
    async (ctx: AppContext) => {
      try {
        ctx.sendEvent(events.HEADER_GO_BACK_CLICKED);
        if (session) {
          emitNavigationTracking("back_button_clicked", session, {
            from_page: "payment_confirmation",
          });
        }
        if (submittingGoBack) return;

        // If user is on the add new card form view (with saved cards present),
        // go back to the payment method list instead of plan selection
        if (showNewCardFormView) {
          handleInlineCardFormBack();
          return;
        }

        setSubmittingGoBack(true);
        const updatedSession = await backFromPaymentMethodSelection();
        setSubmittingGoBack(false);
        onSessionFetched(updatedSession);
      } catch (error) {
        setSubmittingGoBack(false);
        logError(
          "payment-confirmation-new",
          "Failed to go back from payment method selection",
          error instanceof Error ? error : new Error(String(error)),
          { error },
        );
        ctx.navigate(Page.PAYMENT_PLAN_SELECTION);
      }
    },
    [
      session,
      submittingGoBack,
      showNewCardFormView,
      handleInlineCardFormBack,
      backFromPaymentMethodSelection,
      onSessionFetched,
    ],
  );

  const handleFooterAgreeChange = useCallback((agreed: boolean) => {
    hasAgreementBeenInteractedRef.current = true;
    setIsAgreed(agreed);
    setShowError(false);
  }, []);

  const handlePaymentMethodSelect = useCallback(
    (method: Parameters<typeof handleSelectPaymentMethod>[0] | "new-card") => {
      if (method === "new-card") {
        setShowNewCardFormView(true);
        setShowInlineCardForm(true);
        setSelectedPaymentMethod({
          type: "card",
          id: "new",
          name: "",
          last4: "",
          icon: "",
        });
      } else {
        handleSelectPaymentMethod(method);
        setShowInlineCardForm(false);
        setShowNewCardFormView(false);
      }
    },
    [handleSelectPaymentMethod, setSelectedPaymentMethod],
  );

  // Apple Pay handler
  useEffect(() => {
    if (ctxRef.current) {
      applePayClickHandlerRef.current = async () => {
        if (processing !== Processing.NONE) return;
        if (
          PaymentMethodUtils.shouldPayWithWalletOnly(
            isWalletSelected,
            ctxRef.current?.session.paymentDetails?.canWalletCoverDownPayment,
          )
        ) {
          return;
        }
        await submitWithApplePay(ctxRef.current!, isWalletSelected, {
          shouldUseWalletBalance: isWalletSelected,
          documentsSigned: !documentSignatureIsPending,
          termsAccepted: documentSignatureIsPending ? isAgreed : true,
        });
      };
    }
  }, [
    processing,
    isWalletSelected,
    submitWithApplePay,
    session,
    documentSignatureIsPending,
    isAgreed,
  ]);

  // Use a ref to track if we should show Apple Pay modal (computed in render)
  const shouldShowApplePayModalRef = useRef(false);

  useEffect(() => {
    if (
      shouldShowApplePayModalRef.current &&
      !isApplePayModalOpen &&
      !hasManuallyClosedApplePayModalRef.current
    ) {
      openApplePayModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApplePayModalOpen]);

  const confirmHeadingText = useMemo(() => {
    if (selectedPaymentOption?.tenure === 1) {
      return t("payment.pnm_heading");
    }

    if (isWalletSelected) {
      if (session?.paymentDetails?.canWalletCoverDownPayment) {
        return t("payment.wallet_covers_today");
      }

      const walletReducedAmount =
        session?.paymentDetails?.cardDownPaymentAmountWithWallet?.amount;
      const walletReducedCurrency =
        session?.paymentDetails?.cardDownPaymentAmountWithWallet?.currency ??
        session?.order?.currency;

      if (walletReducedAmount != null && walletReducedCurrency) {
        const translationParts = t("payment.pay_amount_today", {
          amount: "{{AMOUNT_PLACEHOLDER}}",
        }).split("{{AMOUNT_PLACEHOLDER}}");

        return (
          <>
            <span>{translationParts[0]}</span>
            <MoneyAmount
              amount={walletReducedAmount}
              currency={walletReducedCurrency}
              language={i18n.language}
              className="inline-flex"
            />
            <span>{translationParts[1]}</span>
          </>
        );
      }
    }

    // For tenure > 1, the "Pay X today" header reflects the down-payment
    // (first scheduled charge), not the recurring instalment.
    const amount = selectedPaymentOption?.downPaymentAmount?.amount;
    const currency =
      selectedPaymentOption?.downPaymentAmount?.currency ??
      session?.order?.currency;

    if (!amount || !currency) {
      return t("payment.confirm_and_pay");
    }

    // Split the translation string and insert MoneyAmount component
    // Using the same pattern as order-details-modal.tsx
    const translationParts = t("payment.pay_amount_today", {
      amount: "{{AMOUNT_PLACEHOLDER}}",
    }).split("{{AMOUNT_PLACEHOLDER}}");

    return (
      <>
        <span>{translationParts[0]}</span>
        <MoneyAmount
          amount={amount}
          currency={currency}
          language={i18n.language}
          className="inline-flex"
        />
        <span>{translationParts[1]}</span>
      </>
    );
  }, [
    isWalletSelected,
    session?.paymentDetails,
    session?.order?.currency,
    selectedPaymentOption?.tenure,
    selectedPaymentOption?.downPaymentAmount,
    t,
    i18n,
  ]);

  const walletBalance = session?.paymentDetails?.availableWalletBalance ?? {
    amount: 0,
    currency: "",
  };
  const hasWalletBalance = walletBalance.amount > 0;

  const handleWalletToggle = useCallback(
    (value: boolean) => {
      if (isPnm) return;
      const ctx = ctxRef.current;
      if (ctx) {
        ctx.sendEvent(
          value ? events.WALLET_SELECTED : events.WALLET_DESELECTED,
          { wallet_balance: walletBalance.amount },
        );
      }
      setIsWalletSelected(value);
    },
    [isPnm, walletBalance.amount, setIsWalletSelected],
  );

  // For PNM, ensure wallet is never selected
  useEffect(() => {
    if (isPnm && isWalletSelected) {
      setIsWalletSelected(false);
    }
  }, [isPnm, isWalletSelected, setIsWalletSelected]);

  // Loading page
  const loadingPage = useMemo(
    () => (
      <div className="fixed inset-0 bg-gray-100 flex flex-col z-10">
        <div className="w-full md:w-[480px] mx-auto bg-white min-h-screen md:rounded-t-[32px] md:min-h-[calc(100vh-24px)] md:mt-[24px] md:shadow-xs flex flex-col">
          <LoadingPage
            title={
              submittingGoBack
                ? t("loading.go_back_to_payment_plan")
                : t("loading.processing_payment")
            }
            message={t("loading.do_not_refresh")}
          />
        </div>
      </div>
    ),
    [t, submittingGoBack],
  );

  return (
    <>
      <AppWrapper
        visible={true}
        showLanguageSwitcher={true}
        showLogo={shouldShowLoading || hideBack}
        showCartButton={false}
        headerLeft={
          shouldShowLoading || hideBack
            ? undefined
            : (ctx) => {
                ctxRef.current = ctx;
                return (
                  <Button
                    onClick={() => handleHeaderBackClick(ctx)}
                    variant="neutral"
                    rounded
                    size="medium"
                    data-testid="payment-confirmation-back-button"
                    disabled={
                      processing !== Processing.NONE || submittingGoBack
                    }
                  >
                    {ctx.localizedIcon(LOCALIZED_ICON_TYPE.ARROW_BACK)}
                  </Button>
                );
              }
        }
        stickyFooter={
          shouldShowLoading || isIvrFailed || !session
            ? undefined
            : (ctx: AppContext) => {
                ctxRef.current = ctx;
                return (
                  <>
                    {footerAbovePaySlot?.(ctx, { processing })}
                    <PaymentFooter
                      ctx={ctx}
                      session={session}
                      isAgreed={isAgreed}
                      onAgreeChange={handleFooterAgreeChange}
                      showMurabahaDocumentSigningCheckbox={
                        showMurabahaDocumentSigningCheckbox
                      }
                      isUaeBnplPlus={isUaeBnplPlus}
                      isWalletSelected={isWalletSelected}
                      selectedPaymentMethod={selectedPaymentMethod}
                      isPnm={isPnm}
                      documentSignatureIsPending={documentSignatureIsPending}
                      processing={processing}
                      isProcessingPayment={isProcessingPayment}
                      submittingGoBack={submittingGoBack}
                      onPayClick={handlePayClick}
                      onDocumentsClick={openMurabahaDocumentModal}
                      showError={showError}
                      validateCardForm={validateCardForm}
                    />
                  </>
                );
              }
        }
        stickyFooterWithShadow={true}
      >
        {(ctx) => {
          ctxRef.current = ctx;

          const [hasPaymentError, paymentError] = session
            ? parsePaymentResultLib(
                session.paymentResult,
                getPaymentErrorMessage,
              )
            : [false, ""];
          const applePayIsSelected =
            selectedPaymentMethod?.type === "apple_pay";
          const hasSessionError =
            (session?.error?.type ?? SessionError.NONE) !== SessionError.NONE;
          const shouldShowApplePayModal =
            !isPnm &&
            !hasSessionError &&
            !hasPaymentError &&
            applePayIsSelected &&
            ivrStatus === "ACCEPTED" &&
            !walletCoversDownPayment;
          shouldShowApplePayModalRef.current = shouldShowApplePayModal;

          if (shouldShowLoading) {
            return loadingPage;
          }

          if (
            (ivrStatus ?? "").toUpperCase() === "FAILED" ||
            session?.status === CheckoutStatus.INVALIDATED
          ) {
            return <ErrorScreen ctx={ctx} />;
          }

          return (
            <>
              {topSlot ? topSlot(ctx, { isWalletSelected }) : null}
              {hasPaymentError && (
                <div className="px-6 mb-2" data-testid="payment-error-banner">
                  <Banner type={BannerType.ERROR}>{paymentError}</Banner>
                  <p
                    className="mt-2 text-sm text-[#585B7A]"
                    data-testid="payment-error-recovery-hint"
                  >
                    {t("payment_confirmation.error_recovery_hint")}
                  </p>
                </div>
              )}

              <div
                className="flex items-center mb-4 px-6"
                data-testid="payment-confirmation-heading"
              >
                <div
                  className={
                    paymentHeading
                      ? "text-[18px] font-semibold text-[#16181D]"
                      : "text-[24px] font-bold text-[#16181D]"
                  }
                >
                  {paymentHeading ?? confirmHeadingText}
                </div>
              </div>

              {!isPnm &&
                isWalletSelected &&
                (session?.paymentDetails?.walletDownPaymentAmount?.amount ??
                  0) > 0 &&
                (() => {
                  const appliedAmount =
                    session!.paymentDetails!.walletDownPaymentAmount!.amount;
                  const appliedCurrency =
                    session!.paymentDetails!.walletDownPaymentAmount!
                      .currency ??
                    session?.order?.currency ??
                    "";
                  const translationParts = t("payment.wallet_amount_applied", {
                    amount: "{{AMOUNT_PLACEHOLDER}}",
                  }).split("{{AMOUNT_PLACEHOLDER}}");
                  return (
                    <div
                      className="flex items-center gap-1.5 px-6 mb-3"
                      data-testid="wallet-applied-message"
                    >
                      <GreenWalletIcon />
                      <span className="text-[#128351] text-[14px] font-medium">
                        <span>{translationParts[0]}</span>
                        <MoneyAmount
                          amount={appliedAmount}
                          currency={appliedCurrency}
                          language={i18n.language}
                          className="inline-flex"
                        />
                        <span>{translationParts[1]}</span>
                      </span>
                    </div>
                  );
                })()}

              {!hideCancelMessage && (
                <div
                  className="text-sm text-[#585B7A] px-6 mb-6 flex gap-2 items-center"
                  data-testid="payment-cancel-message"
                >
                  <div>
                    <CancelIcon />
                  </div>
                  <div>{t("payment.cancel_message")}</div>
                </div>
              )}

              <div className="space-y-6 px-6 pb-6">
                <div>
                  {/*
                   * When the wallet covers the full down payment, the card /
                   * Apple Pay chooser is kept visible but faded and made inert
                   * (no pointer or keyboard interaction) to signal it is not
                   * applied. Payment is submitted as wallet-only.
                   */}
                  <div
                    className={`transition-opacity duration-200${
                      walletCoversDownPayment
                        ? " opacity-50 pointer-events-none"
                        : ""
                    }`}
                    inert={walletCoversDownPayment}
                    data-testid="payment-methods-chooser"
                  >
                    {(() => {
                      const hasSavedCards =
                        savedCards?.cards && savedCards.cards.length > 0;
                      const savedCardsLoaded = savedCards !== undefined;

                      if (isPnm) {
                        if (!savedCardsLoaded) {
                          return (
                            <div
                              className="border border-gray-200 rounded-[20px] p-6 flex flex-col items-center justify-center gap-4 min-h-[120px]"
                              data-testid="payment-methods-loading"
                            >
                              <CircularProgress
                                size={32}
                                style={{ color: "#9600F1" }}
                              />
                            </div>
                          );
                        }

                        if (hasSavedCards && selectedPaymentOption) {
                          return (
                            <RepaymentDueCard
                              paymentOption={selectedPaymentOption}
                              currency={
                                selectedPaymentOption.totalToBeRepaidAmount
                                  ?.currency ?? ""
                              }
                            />
                          );
                        }

                        return (
                          <>
                            {selectedPaymentOption && (
                              <RepaymentDueCard
                                paymentOption={selectedPaymentOption}
                                currency={
                                  selectedPaymentOption.totalToBeRepaidAmount
                                    ?.currency ?? ""
                                }
                              />
                            )}
                            <div className="mt-6">
                              <InlineCardForm
                                cardForm={cardForm}
                                onCardFormChange={setCardForm}
                                error={error}
                                session={session}
                                rtl={ctx.rtl}
                                hideHeading={hideBack}
                                afterHeading={
                                  <div
                                    className="flex items-center gap-2.5 rounded-xl bg-[#F4F5F8] p-2"
                                    data-testid="pnm-card-verification-message"
                                  >
                                    <div className="shrink-0 mt-0.5">
                                      <InfoOutlinedIcon />
                                    </div>
                                    <p className="text-xs text-[#585B7A] leading-5">
                                      {t("payment.pnm_card_verification", {
                                        amount: `${getCurrencySymbol(stringToCurrency(session?.order?.currency != null ? Currency[session.order.currency] : "SAR"))}\u202f\u00a01`,
                                      })}
                                    </p>
                                  </div>
                                }
                              />
                            </div>
                          </>
                        );
                      }

                      const hasPaymentMethodsToChoose =
                        paymentMethods.length > 0;
                      if (!savedCardsLoaded) {
                        return (
                          <div
                            className="border border-gray-200 rounded-[20px] p-6 flex flex-col items-center justify-center gap-4 min-h-[120px]"
                            data-testid="payment-methods-loading"
                          >
                            <CircularProgress
                              size={32}
                              style={{ color: "#9600F1" }}
                            />
                            <p className="text-sm text-[#585B7A]">
                              {t(
                                "payment_confirmation.loading_payment_methods",
                              )}
                            </p>
                          </div>
                        );
                      }
                      if (!hasPaymentMethodsToChoose) {
                        return (
                          <div className="mt-6">
                            <InlineCardForm
                              cardForm={cardForm}
                              onCardFormChange={setCardForm}
                              error={error}
                              session={session}
                              rtl={ctx.rtl}
                              hideHeading={hideBack}
                            />
                          </div>
                        );
                      }

                      return (
                        <>
                          {!showNewCardFormView ? (
                            <PaymentMethodList
                              paymentMethods={paymentMethods}
                              selectedMethod={selectedPaymentMethod}
                              onMethodSelect={handlePaymentMethodSelect}
                              rtl={ctx.rtl}
                              session={session}
                            />
                          ) : (
                            <div className="mt-6">
                              <InlineCardForm
                                cardForm={cardForm}
                                onCardFormChange={setCardForm}
                                error={error}
                                session={session}
                                rtl={ctx.rtl}
                                hideHeading={hideBack}
                                onBack={
                                  hideBack
                                    ? handleInlineCardFormBack
                                    : undefined
                                }
                              />
                            </div>
                          )}
                          {showInlineCardForm && !showNewCardFormView && (
                            <div className="mt-6">
                              <InlineCardForm
                                cardForm={cardForm}
                                onCardFormChange={setCardForm}
                                error={error}
                                session={session}
                                rtl={ctx.rtl}
                                hideHeading={hideBack}
                              />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {!isPnm && hasWalletBalance && (
                    <div className="mt-4">
                      <WalletBoxNew
                        walletBalance={walletBalance}
                        isSelected={isWalletSelected}
                        onToggle={handleWalletToggle}
                      />
                    </div>
                  )}

                  {showKfsAcknowledgementLine &&
                    session &&
                    keyFactsDocument != null && (
                      <KeyFactsAcknowledgementLine
                        ctx={ctx}
                        session={session}
                        onOpenKfsModal={openKfsDocumentModal}
                      />
                    )}
                </div>
              </div>

              {documentModalKind === "kfs" && (
                <KeyFactsStatementModal
                  ctx={ctx}
                  isOpen={isBottomSheetOpen}
                  onClose={closeBottomSheet}
                />
              )}
              {documentModalKind === "murabaha" && (
                <DocumentsModalNew
                  ctx={ctx}
                  isOpen={isBottomSheetOpen}
                  isClosing={isBottomSheetClosing}
                  onClose={closeBottomSheet}
                  onAgree={(agreed) => {
                    handleFooterAgreeChange(agreed);
                    if (agreed) {
                      closeBottomSheet();
                    }
                  }}
                  isAgreed={isAgreed}
                  documentsList={murabahaDocuments}
                  isUaeBnplPlus={isUaeBnplPlus}
                />
              )}

              {!isPnm && (
                <CvvModal
                  isOpen={cvvModalOpen}
                  onClose={() => {
                    closeCvvModal();
                    setCvv("");
                  }}
                  cvv={cvv}
                  onCvvChange={setCvv}
                  onSubmit={handleCvvSubmit}
                  processing={processing}
                  session={session}
                  selectedPaymentMethodId={selectedPaymentMethod?.id}
                  isWalletSelected={isWalletSelected}
                  documentSignatureIsPending={documentSignatureIsPending}
                  isProcessingPayment={isProcessingPayment}
                />
              )}

              {!isPnm && shouldShowApplePayModal && (
                <ApplePayModal
                  isOpen={isApplePayModalOpen}
                  onClose={closeApplePayModal}
                  onPayClick={() => {
                    applePayClickHandlerRef.current?.()?.catch((error) => {
                      logError(
                        "payment-confirmation-new",
                        "Error processing Apple Pay",
                        error instanceof Error
                          ? error
                          : new Error(String(error)),
                        { error },
                      );
                    });
                  }}
                  downPaymentAmount={PaymentMethodUtils.calculateAmountToPay(
                    ctx,
                    isWalletSelected,
                  )}
                  currency={
                    session?.order?.currency
                      ? Currency[session.order.currency]
                      : undefined
                  }
                />
              )}
            </>
          );
        }}
      </AppWrapper>
    </>
  );
}
